import { useState, useEffect } from "react";
import api from "../../utils/api";
import { Info, Calculator } from "lucide-react";
import { calcEffectiveHours } from "../../utils/calculations";

const DOMAINS = ["Insurance", "Banking & Fintech", "Healthcare / BPO", "Telecom", "Retail / eCommerce", "Logistics", "Government Services", "Other / Generic", "Custom"];
const CURRENCIES = ["USD", "AED", "SAR", "OMR", "INR", "GBP", "EUR"];

function FieldGroup({ title, children }) {
  return (
    <div className="bg-white border border-[#E2E8E3] rounded-md p-5 mb-4">
      <h3 className="font-heading text-base font-semibold text-sage-800 mb-4 pb-3 border-b border-sage-50">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, tooltip, children }) {
  return (
    <div>
      <div className="flex items-center gap-1 mb-1.5">
        <label className="text-xs font-semibold uppercase tracking-wider text-sage-600">{label}</label>
        {tooltip && (
          <div className="group relative">
            <Info className="w-3 h-3 text-sage-300 cursor-help" />
            <div className="absolute left-0 bottom-full mb-1 w-52 bg-sage-900 text-white text-xs rounded-md p-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none leading-relaxed">
              {tooltip}
            </div>
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

function NumInput({ value, onChange, min, max, step = 1, ...props }) {
  return (
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      min={min}
      max={max}
      step={step}
      className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none transition-colors bg-white"
      {...props}
    />
  );
}

export default function SetupTab({ project, updateSetup }) {
  const setup = project?.setup || {};
  const org = setup.org_profile || {};
  const vc = setup.volume_capacity || {};
  const qw = setup.quality_waste || {};
  const eh = setup.effective_hours || {};

  const eff = calcEffectiveHours(eh);

  const setOrg = (k, v) => updateSetup("org_profile", { ...org, [k]: v });
  const setVc = (k, v) => updateSetup("volume_capacity", { ...vc, [k]: v });
  const setQw = (k, v) => updateSetup("quality_waste", { ...qw, [k]: v });
  const setEh = (k, v) => updateSetup("effective_hours", { ...eh, [k]: v });

  return (
    <div className="animate-fade-in">
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Step 1</p>
        <h2 className="font-heading text-2xl font-semibold text-sage-900">Setup & Context</h2>
        <p className="text-sage-500 text-sm mt-1">Configure the organisation profile, volume inputs, and effective FTE hours calculator</p>
      </div>

      <FieldGroup title="4.1 Organisation Profile">
        <div className="md:col-span-2">
          <Field label="Company / Organisation Name" tooltip="Name of the client organisation or department being modelled">
            <input type="text" value={org.company_name || ""} onChange={(e) => setOrg("company_name", e.target.value)} placeholder="e.g. Gulf Insurance Company" className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none" data-testid="setup-company-name" />
          </Field>
        </div>
        <Field label="Department / Business Unit">
          <input type="text" value={org.department || ""} onChange={(e) => setOrg("department", e.target.value)} placeholder="e.g. Claims Operations" className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none" />
        </Field>
        <Field label="Country / Region">
          <input type="text" value={org.country || ""} onChange={(e) => setOrg("country", e.target.value)} placeholder="e.g. UAE, Saudi Arabia" className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none" />
        </Field>
        <Field label="Business Domain">
          <select value={org.domain || "Other / Generic"} onChange={(e) => setOrg("domain", e.target.value)} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none bg-white" data-testid="setup-domain-select">
            {DOMAINS.map((d) => <option key={d}>{d}</option>)}
          </select>
        </Field>
        <Field label="Currency">
          <select value={org.currency || "USD"} onChange={(e) => setOrg("currency", e.target.value)} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none bg-white">
            {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Localisation Target %" tooltip="Regulatory floor for local nationals e.g. Emiratisation 20%, Saudisation 30%">
          <NumInput value={org.localization_target ?? 20} onChange={(v) => setOrg("localization_target", v)} min={0} max={100} data-testid="setup-localization-target" />
        </Field>
        <Field label="Tolerance Band ±pp" tooltip="How far from the target before a stage is flagged with a compliance warning">
          <NumInput value={org.tolerance_band ?? 5} onChange={(v) => setOrg("tolerance_band", v)} min={0} max={20} />
        </Field>
        <div className="md:col-span-2 lg:col-span-3">
          <Field label="Additional Notes / Context">
            <textarea value={org.notes || ""} onChange={(e) => setOrg("notes", e.target.value)} rows={2} placeholder="Any relevant context about this engagement..." className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none resize-none" />
          </Field>
        </div>
      </FieldGroup>

      <FieldGroup title="4.2 Volume & Capacity Inputs">
        <Field label="Total Monthly Volume" tooltip="Total transactions/cases/tickets processed per month across all stages">
          <NumInput value={vc.total_monthly_volume ?? 1000} onChange={(v) => setVc("total_monthly_volume", v)} min={0} data-testid="setup-monthly-volume" />
        </Field>
        <Field label="Working Days per Month">
          <NumInput value={vc.working_days_per_month ?? 22} onChange={(v) => setVc("working_days_per_month", v)} min={1} max={31} />
        </Field>
        <Field label="Avg Cost per FTE/Month" tooltip="Average fully loaded cost per FTE including salary, benefits, and overheads">
          <NumInput value={vc.cost_per_fte ?? 15000} onChange={(v) => setVc("cost_per_fte", v)} min={0} data-testid="setup-cost-per-fte" />
        </Field>
        <Field label="Target SLA (% on-time)" tooltip="Target percentage of cases completed within the agreed service level">
          <NumInput value={vc.target_sla ?? 95} onChange={(v) => setVc("target_sla", v)} min={0} max={100} step={0.5} />
        </Field>
      </FieldGroup>

      <FieldGroup title="4.3 Quality & Waste Inputs">
        <Field label="Rework / Error Rate %" tooltip="Percentage of total volume requiring rework due to errors">
          <NumInput value={qw.rework_rate ?? 5} onChange={(v) => setQw("rework_rate", v)} min={0} max={100} step={0.5} data-testid="setup-rework-rate" />
        </Field>
        <Field label="NVA Time %" tooltip="Percentage of shift time spent on non-value-adding activities">
          <NumInput value={qw.nva_time ?? 20} onChange={(v) => setQw("nva_time", v)} min={0} max={100} step={0.5} data-testid="setup-nva-time" />
        </Field>
        <Field label="Absenteeism Rate %" tooltip="Percentage of working days lost to unplanned absence per year">
          <NumInput value={qw.absenteeism_rate ?? 3} onChange={(v) => setQw("absenteeism_rate", v)} min={0} max={100} step={0.5} />
        </Field>
        <Field label="Attrition Rate % (Annual)">
          <NumInput value={qw.attrition_rate ?? 15} onChange={(v) => setQw("attrition_rate", v)} min={0} max={100} step={0.5} />
        </Field>
        <Field label="Onboarding Duration (weeks)" tooltip="Average time to full productivity for a new hire">
          <NumInput value={qw.onboarding_weeks ?? 4} onChange={(v) => setQw("onboarding_weeks", v)} min={0} max={52} />
        </Field>
      </FieldGroup>

      {/* Effective FTE Hours Calculator */}
      <div className="bg-white border border-[#E2E8E3] rounded-md p-5 mb-4">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-sage-50">
          <Calculator className="w-4 h-4 text-sage-500" />
          <h3 className="font-heading text-base font-semibold text-sage-800">5. Effective FTE Hours Calculator</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-3">Annual Calendar</h4>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Gross Working Days">
                <NumInput value={eh.gross_working_days ?? 260} onChange={(v) => setEh("gross_working_days", v)} min={200} max={365} />
              </Field>
              <Field label="Public Holidays">
                <NumInput value={eh.public_holidays ?? 10} onChange={(v) => setEh("public_holidays", v)} min={0} max={50} />
              </Field>
              <Field label="Annual Leave — Expat (days)">
                <NumInput value={eh.annual_leave_expat ?? 21} onChange={(v) => setEh("annual_leave_expat", v)} min={0} max={60} />
              </Field>
              <Field label="Annual Leave — Local (days)">
                <NumInput value={eh.annual_leave_local ?? 30} onChange={(v) => setEh("annual_leave_local", v)} min={0} max={60} />
              </Field>
              <Field label="Sick Leave (days)">
                <NumInput value={eh.sick_leave_days ?? 5} onChange={(v) => setEh("sick_leave_days", v)} min={0} max={30} />
              </Field>
              <Field label="Other Absence (days)">
                <NumInput value={eh.other_absence_days ?? 3} onChange={(v) => setEh("other_absence_days", v)} min={0} max={30} />
              </Field>
            </div>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-3">Daily Hours</h4>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Shift Hours — Expat">
                <NumInput value={eh.shift_hours_expat ?? 8} onChange={(v) => setEh("shift_hours_expat", v)} min={0} max={12} step={0.5} />
              </Field>
              <Field label="Shift Hours — Local" tooltip="UAE law: default 5 hours during Ramadan, typically 5-6 hours">
                <NumInput value={eh.shift_hours_local ?? 5} onChange={(v) => setEh("shift_hours_local", v)} min={0} max={12} step={0.5} />
              </Field>
              <Field label="Prayer / Breaks (hrs)">
                <NumInput value={eh.prayer_break ?? 0.5} onChange={(v) => setEh("prayer_break", v)} min={0} max={3} step={0.25} />
              </Field>
              <Field label="Lunch Break (hrs)">
                <NumInput value={eh.lunch_break ?? 1} onChange={(v) => setEh("lunch_break", v)} min={0} max={2} step={0.25} />
              </Field>
            </div>
          </div>
        </div>

        {/* Live outputs */}
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 bg-sage-50 rounded-md p-4">
          {[
            { label: "Monthly Hrs — Expat", value: eff.monthlyHrsExpat?.toFixed(1) || "—" },
            { label: "Monthly Hrs — Local", value: eff.monthlyHrsLocal?.toFixed(1) || "—" },
            { label: "Conversion Factor", value: `1 local = ${eff.conversionFactor}× expat`, small: true },
            { label: "Capacity Gap %", value: `${eff.capacityGapPct}%`, color: Number(eff.capacityGapPct) > 20 ? "text-amber-600" : "text-sage-700" },
          ].map(({ label, value, small, color }) => (
            <div key={label} className="text-center">
              <div className={`font-heading font-semibold ${small ? "text-base" : "text-2xl"} ${color || "text-sage-700"}`} data-testid={`eff-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</div>
              <div className="text-xs text-sage-500 mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
